import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { makeStyles } from "@mui/styles";
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Typography,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CampaignIcon from "@mui/icons-material/Campaign";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

import { getOrders, updateOrderStatus } from "apis/orderAPIs";
import { showErrorSnackbar, showSuccessSnackbar } from "store/actions/snackbarMessage";
import { transformOrderId } from "util/orderUtils";
import {
  BUCKET_READY,
  KIND_PAGER,
  boardBucket,
  callingNumber,
  callingQueryRange,
  nextAction,
  orderPartition,
  selectCallableOrders,
  waitingMinutes,
} from "util/orderStatus";

const REFRESH_MS = 5000;
// Long enough that a slow-moving kitchen still looks calm, short enough that a
// forgotten order stands out.
const OVERDUE_MINUTES = 10;

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    // The app ships no CssBaseline, so the plain elements below would otherwise
    // fall back to the browser's serif default.
    fontFamily: theme.typography.fontFamily,
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1.5),
    },
  },
  header: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  headerText: {
    minWidth: 0,
  },
  subtitle: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  columns: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
    gap: theme.spacing(3),
    alignItems: "start",
  },
  column: {
    minWidth: 0,
  },
  columnHeading: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  columnLabel: {
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    fontSize: "0.8rem",
  },
  labelPreparing: { color: theme.palette.warning.dark },
  labelReady: { color: theme.palette.success.dark },
  cards: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
  },
  card: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: 12,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    // A left rule carries the state without repeating a chip on every row.
    borderLeftWidth: 6,
    borderLeftStyle: "solid",
  },
  cardPreparing: { borderLeftColor: theme.palette.warning.main },
  cardReady: { borderLeftColor: theme.palette.success.main },
  cardBody: {
    flex: 1,
    minWidth: 0,
  },
  cardKind: {
    fontSize: "0.65rem",
    fontWeight: 700,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
  },
  orderRef: {
    fontWeight: 700,
    fontSize: "1.75rem",
    lineHeight: 1.1,
    fontVariantNumeric: "tabular-nums",
    overflowWrap: "anywhere",
  },
  orderRefHint: {
    fontVariantNumeric: "tabular-nums",
  },
  meta: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.75),
    color: theme.palette.text.secondary,
    fontSize: "0.8rem",
  },
  overdue: {
    color: theme.palette.error.main,
    fontWeight: 600,
  },
  action: {
    minWidth: 148,
    // Staff tap this on a tablet, often in a hurry.
    minHeight: 52,
    fontWeight: 700,
    whiteSpace: "nowrap",
  },
  empty: {
    padding: theme.spacing(4, 2),
    textAlign: "center",
    color: theme.palette.text.secondary,
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: 12,
  },
  progress: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(6),
  },
}));

const OrderCard = ({ order, now, isBusy, onAdvance }) => {
  const classes = useStyles();

  const bucket = boardBucket(order.status);
  const isReady = bucket === BUCKET_READY;
  const action = nextAction(order);
  const waited = waitingMinutes(order, now);
  const isOverdue = !isReady && waited >= OVERDUE_MINUTES;
  const orderType = (order.order && order.order.type) || "";
  // The same number the customer display calls, so staff and customers never
  // read two different numbers off the same order.
  const called = callingNumber(order);
  const isPager = called.kind === KIND_PAGER;

  return (
    <article className={`${classes.card} ${isReady ? classes.cardReady : classes.cardPreparing}`}>
      <div className={classes.cardBody}>
        <div className={classes.cardKind}>{isPager ? "Pager" : "Order"}</div>
        <div className={classes.orderRef}>
          {isPager ? called.ref : `#${called.ref}`}
        </div>
        <div className={classes.meta}>
          <span className={isOverdue ? classes.overdue : undefined}>
            {waited === 0 ? "just now" : `waiting ${waited} min`}
          </span>
          {orderType && <Chip label={orderType} size="small" variant="outlined" />}
          {/* Staff search and refunds work off the order reference, so keep it
              visible even when the pager number is what gets called. */}
          {isPager && (
            <span className={classes.orderRefHint}>#{transformOrderId(order.id)}</span>
          )}
        </div>
      </div>

      {action && (
        <Button
          className={classes.action}
          color={isReady ? "primary" : "success"}
          disabled={isBusy}
          onClick={() => onAdvance(order, action)}
          startIcon={isReady ? <CheckCircleOutlineIcon /> : <CampaignIcon />}
          variant="contained"
        >
          {action.label}
        </Button>
      )}
    </article>
  );
};

const orderShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  status: PropTypes.string,
  statusUpdatedAt: PropTypes.any,
  createdAt: PropTypes.any,
  yearStr: PropTypes.string,
  dateStr: PropTypes.string,
  order: PropTypes.shape({ type: PropTypes.string }),
});

OrderCard.propTypes = {
  order: orderShape.isRequired,
  now: PropTypes.number.isRequired,
  isBusy: PropTypes.bool,
  onAdvance: PropTypes.func.isRequired,
};

const OrderColumn = ({ label, variant, orders, emptyText, now, busyOrderId, onAdvance }) => {
  const classes = useStyles();
  const isReady = variant === BUCKET_READY;

  return (
    <section className={classes.column}>
      <header className={classes.columnHeading}>
        <span
          className={`${classes.columnLabel} ${isReady ? classes.labelReady : classes.labelPreparing}`}
        >
          {label}
        </span>
        <Chip label={orders.length} size="small" />
      </header>

      {orders.length === 0 ? (
        <p className={classes.empty}>{emptyText}</p>
      ) : (
        <div className={classes.cards}>
          {orders.map((order) => (
            <OrderCard
              isBusy={busyOrderId === order.id}
              key={order.id}
              now={now}
              onAdvance={onAdvance}
              order={order}
            />
          ))}
        </div>
      )}
    </section>
  );
};

/**
 * Staff-facing calling page.
 *
 * Rendered inside MainLayout, which already gates on `/user` and bounces an
 * unauthenticated visitor to sign-in - this page needs no auth logic of its own.
 * It writes through the same endpoint the iPad POS uses, so an order marked
 * ready here shows as ready on the POS and vice versa.
 */
OrderColumn.propTypes = {
  label: PropTypes.string.isRequired,
  variant: PropTypes.string.isRequired,
  orders: PropTypes.arrayOf(orderShape).isRequired,
  emptyText: PropTypes.string.isRequired,
  now: PropTypes.number.isRequired,
  busyOrderId: PropTypes.string,
  onAdvance: PropTypes.func.isRequired,
};

const OrderCalling = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const queryClient = useQueryClient();

  const restaurantId = useSelector((state) => state.account.user && state.account.user.restaurantId);

  const [busyOrderId, setBusyOrderId] = useState(null);
  // Just after midnight the orders still cooking sit in yesterday's partition,
  // so the range widens to match what the customer board is already showing.
  const { fromDate, toDate } = callingQueryRange();

  const {
    data: allOrders = [],
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["orders", "online", fromDate, toDate],
    queryFn: () => getOrders("online", fromDate, toDate, null),
    refetchInterval: REFRESH_MS,
    refetchIntervalInBackground: true,
    staleTime: 0,
    retry: 2,
  });

  const statusMutation = useMutation({
    mutationFn: updateOrderStatus,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["orders"] });
    },
    onError: (mutationError) => {
      console.error("Failed to update order status", mutationError);
      dispatch(showErrorSnackbar("Could not update the order. Please try again."));
    },
    onSettled: () => setBusyOrderId(null),
  });

  // Recomputed on every poll, so ages and expiry stay honest without a timer.
  const callable = useMemo(() => selectCallableOrders(allOrders, Date.now()), [allOrders]);
  const ready = callable.filter((order) => boardBucket(order.status) === BUCKET_READY);
  const preparing = callable.filter((order) => boardBucket(order.status) !== BUCKET_READY);

  const handleAdvance = (order, action) => {
    const partition = orderPartition(order);
    if (!partition) {
      // Without a day partition the write would land on the wrong document.
      dispatch(showErrorSnackbar("This order is missing its order date and cannot be updated."));
      return;
    }

    setBusyOrderId(order.id);
    statusMutation.mutate(
      {
        orderNumber: order.id,
        yearString: partition.yearString,
        dateString: partition.dateString,
        status: action.status,
      },
      {
        onSuccess: () => {
          const called = callingNumber(order);
          const name =
            called.kind === KIND_PAGER ? `Pager ${called.ref}` : `Order #${called.ref}`;
          dispatch(
            showSuccessSnackbar(
              action.status === "collected"
                ? `${name} collected`
                : `${name} is ready for pickup`
            )
          );
        },
      }
    );
  };

  // Navigating has to happen after render, not during it.
  useEffect(() => {
    const status = error && error.response && error.response.status;
    if (status === 401 || status === 403) {
      history.push("/sign-in");
    }
  }, [error, history]);

  return (
    <div className={classes.root}>
      <header className={classes.header}>
        <div className={classes.headerText}>
          <Typography variant="h4">Order calling</Typography>
          <Typography className={classes.subtitle} variant="body1">
            Mark orders ready to call them onto the in-store display, then mark them
            collected once the customer has picked up.
          </Typography>
        </div>

        {restaurantId && (
          <Button
            color="primary"
            endIcon={<OpenInNewIcon />}
            href={`/calling/${restaurantId}`}
            rel="noopener noreferrer"
            target="_blank"
            variant="outlined"
          >
            Open display board
          </Button>
        )}
      </header>

      {isError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Could not load today&apos;s orders. Retrying automatically.
        </Alert>
      )}

      {isLoading ? (
        <div className={classes.progress}>
          <CircularProgress />
        </div>
      ) : (
        <div className={classes.columns}>
          <OrderColumn
            busyOrderId={busyOrderId}
            emptyText="No orders in the kitchen right now."
            label="Preparing"
            now={Date.now()}
            onAdvance={handleAdvance}
            orders={preparing}
            variant="preparing"
          />
          <OrderColumn
            busyOrderId={busyOrderId}
            emptyText="Nothing waiting to be collected."
            label="Ready for pickup"
            now={Date.now()}
            onAdvance={handleAdvance}
            orders={ready}
            variant={BUCKET_READY}
          />
        </div>
      )}
    </div>
  );
};

export default OrderCalling;
